import {
  createContext,
  useMemo,
  useReducer,
  useContext,
  useState,
  useEffect
} from "react";
import { socket } from "../socket";
import { useNavigate } from "react-router";
import { Stack } from "@mui/material";
import BTSnackbar from "../components/BTSnackbar";
import secureLocalStorage from "react-secure-storage";
import { manage_socket } from "../store/functions";
import Notifications from "@mui/icons-material/Notifications";
import authService from "../services/auth-service";
const NotificationContext = createContext();
const AppContext = createContext();
const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  register: () => {},
  logout: () => {}
});
const LoadingContext = createContext();
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "SIDENAV_WIDTH": {
      return { ...state, sideNavWidth: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CHAT": {
      return { ...state, openChat: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
const NotificationsProvider = (props) => {
  const [notifications, setNotifications] = useState([]);
  const addNotification = (notification) => {
    const id = Math.random().toString(36).substring(2, 9);
    setNotifications((prev) => [
      {
        id,
        ...notification
      },
      ...prev
    ]);
  };
  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  };
  return (
    <NotificationContext.Provider
      value={{
        addNotification,
        removeNotification
      }}>
      <Stack
        spacing={15}
        position={"fixed"}
        top={"2%"}
        right={"1%"}
        zIndex={"99999"}>
        {notifications.map((content, index) => (
          <BTSnackbar
            key={index}
            color={content.color}
            icon={content.icon}
            title={content.title}
            content={content.message}
            dateTime={content.time || null}
            open={content?.id ? true : false}
            onClose={() => removeNotification(content?.id)}
            close={() => removeNotification(content?.id)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
          />
        ))}
      </Stack>
      {props.children}
    </NotificationContext.Provider>
  );
};
const AppProvider = ({ children }) => {
  const initialState = {
    miniSidenav: false,
    sideNavWidth: 240,
    transparentSidenav: false,
    whiteSidenav: true,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    layout: null,
    isMobile: false,
    darkMode: false
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [state, dispatch], [state, dispatch]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

const AuthContextProvider = (props) => {
  const [userdata, setUserdata] = useState(
    secureLocalStorage?.getItem("userdata")
  );
  let { children } = props;
  const [isAuthenticated, setIsAuthenticated] = useState(!!userdata);
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  useEffect(() => {
    if (!userdata?.u_id) {
      if (socket.connected) {
        socket.disconnect();
      }
      return;
    }
    const handleGetUpdates = async (data) => {
      if (data?.source === "socket") return;
      if (!data?.u_id || userdata.u_id === data.u_id) return;

      try {
        const response = await authService?.get_updates({
          attributes: {
            u_id: userdata?.u_id,
            parent_u_id: userdata?.parent_u_id
          }
        });

        if (response?.status === 1) {
          manage_socket(response);
          setRefresh((prev) => !prev);
          // addNotification({
          //   icon: <Notifications color="white" />,
          //   title: "Success!",
          //   message: response?.message,
          //   color: "success"
          // });
        }
      } catch (error) {
        addNotification({
          icon: <Notifications color="white" />,
          title: "Error!",
          message: error?.response?.data?.message || "Something went wrong",
          color: "error"
        });
      }
    };
    socket.on("get_updates", handleGetUpdates);
    return () => {
      socket.off("get_updates", handleGetUpdates);
    };
  }, [userdata?.u_id, userdata?.parent_u_id]);

  // Handle authentication state changes
  useEffect(() => {
    const userId = userdata?.u_id;

    // logout case
    if (!userId) {
      if (socket.connected) {
        socket.disconnect();
      }
      return;
    }

    // login / first connect
    if (!socket.connected) {
      socket.connect();

      // emit login ONLY after actual connect
      socket.once("connect", () => {
        socket.emit("user-login", userId);
      });
    }

    // cleanup ONLY when userId changes or component unmounts
    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, [userdata?.u_id, socket]);

  const login = async (data) => {
    const userData = {
      ...data,
      label: data?.first_name + " " + data?.last_name
    };

    secureLocalStorage.setItem("access_token", data?.access_token);
    secureLocalStorage.setItem("userdata", userData);
    setUserdata(userData);
    setIsAuthenticated(true);
    navigate("/dashboard");
  };

  const logout = async () => {
    secureLocalStorage.clear();
    setUserdata(null);
    setIsAuthenticated(false);
    navigate("/sign-in");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const showLoading = () => setLoading(true);
  const hideLoading = () => setLoading(false);

  return (
    <LoadingContext.Provider value={{ loading, showLoading, hideLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};
function useBTUIController() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useBTUIController must be used within a BTUIProvider");
  }
  return context;
}

function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useBTUIController must be used within a BTUIProvider");
  }
  return context;
}
const useLoading = () => {
  return useContext(LoadingContext);
};
const setSideNavWidth = (dispatch, value) =>
  dispatch({ type: "SIDENAV_WIDTH", value });

const setMiniSidenav = (dispatch, value) =>
  dispatch({ type: "MINI_SIDENAV", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
export {
  AppProvider,
  AuthContextProvider,
  AuthContext,
  NotificationContext,
  NotificationsProvider,
  LoadingProvider,
  useBTUIController,
  useNotification,
  useLoading,
  setLayout,
  setMiniSidenav,
  setSideNavWidth
};
